/**
 * Typy przeglądarki bazy tenanta (panel ITE).
 *
 * Kontrakt odpowiedzi tras `/api/admin/tenants/:tenantId/database/*` — strukturę zwraca
 * katalog Postgresa (pg_catalog) schematu TEGO tenanta, wiersze wyniku przychodzą już
 * sformatowane do wyświetlenia (string | null), kolumny wrażliwe zamaskowane po stronie API.
 */

export type DbRelationKind = 'table' | 'view' | 'matview';
export type DbRoutineKind = 'function' | 'procedure' | 'trigger_function';

export interface DbColumn {
  name: string;
  /** Typ w notacji Postgresa (`format_type`), np. `varchar(10)`, `timestamptz`. */
  type: string;
  nullable: boolean;
  isPrimaryKey: boolean;
  foreignKey: { table: string; column: string } | null;
  /** Kolumna z danymi wrażliwymi — w wynikach zawsze zamaskowana. */
  sensitive: boolean;
  defaultValue: string | null;
}

export interface DbIndex {
  name: string;
  definition: string;
  unique: boolean;
  primary: boolean;
}

export interface DbConstraint {
  name: string;
  /** p = klucz główny, f = obcy, u = unikalny, c = check. */
  type: 'p' | 'f' | 'u' | 'c';
  definition: string;
}

export interface DbTrigger {
  name: string;
  definition: string;
}

export interface DbRelation {
  name: string;
  kind: DbRelationKind;
  /** Szacunkowa liczba wierszy z katalogu (`reltuples`); null = brak statystyk. */
  rowEstimate: number | null;
  comment: string | null;
  columns: DbColumn[];
  indexes: DbIndex[];
  constraints: DbConstraint[];
  triggers: DbTrigger[];
}

export interface DbRoutine {
  name: string;
  kind: DbRoutineKind;
  /** Argumenty w notacji `pg_get_function_identity_arguments`. */
  args: string;
  returns: string;
  language: string;
}

export interface DbSchemaSnapshot {
  tenant: { id: string; name: string; code: string; dbMode: 'shared' | 'dedicated' };
  database: string;
  schema: string;
  relations: DbRelation[];
  routines: DbRoutine[];
  sequences: string[];
  types: string[];
}

/** Obiekt, którego szczegóły pokazują zakładki Właściwości / SQL / Statystyki / Zależności. */
export type DbObjectRef =
  | { kind: DbRelationKind; name: string }
  | { kind: DbRoutineKind; name: string; args: string };

export interface DbKeyValue {
  key: string;
  value: string;
}

export interface DbDependency {
  type: string;
  name: string;
  relation: string;
}

export interface DbObjectDetails {
  properties: DbKeyValue[];
  ddl: string;
  /** Tylko tabele i widoki zmaterializowane. */
  stats: DbKeyValue[] | null;
  dependencies: DbDependency[];
}

export interface DbResultColumn {
  name: string;
  type: string;
  masked: boolean;
}

export interface DbQueryResult {
  columns: DbResultColumn[];
  rows: (string | null)[][];
  /** Wynik dłuższy niż limit — pokazano tylko `limit` pierwszych wierszy. */
  truncated: boolean;
  durationMs: number;
  /** Plan zapytania (tekst EXPLAIN) — gdy zapytanie szło w trybie „Wyjaśnij". */
  plan: string | null;
  notices: string[];
}

export type DbQueryErrorCode =
  | 'SQL_EMPTY'
  | 'SQL_FORBIDDEN_OBJECT'
  | 'SQL_NOT_READONLY'
  | 'SQL_MULTIPLE_STATEMENTS'
  | 'SQL_PARSE_ERROR'
  | 'SQL_SENSITIVE_COLUMN'
  | 'SQL_FORBIDDEN_FUNCTION'
  | 'SQL_TIMEOUT'
  | 'SQL_EXECUTION_FAILED'
  | 'RATE_LIMITED';

export interface DbQueryError {
  error: DbQueryErrorCode;
  /** Komunikat Postgresa albo walidatora (bez tłumaczenia — pokazywany w „Komunikatach"). */
  message: string;
  /** Pozycja błędu w SQL (1-based, znakowo) — edytor podkreśla to miejsce. */
  position?: number;
}

export const DB_QUERY_LIMITS = [100, 1000] as const;
export type DbQueryLimit = (typeof DB_QUERY_LIMITS)[number];
